import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { getApiErrorBodyFromUnknownError } from "@/features/error/handleError";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";

type McpApiKey = InterludeTypes["McpApiKey"];

interface RevokeMcpConnectionModalProps {
  open: boolean;
  connection: McpApiKey | null;
  onClose: () => void;
  onSuccess: () => void;
}

const styles = {
  warningList: {
    margin: 0,
    paddingLeft: "20px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

type RevokeMcpConnectionModalContentProps = Omit<
  RevokeMcpConnectionModalProps,
  "connection"
> & {
  connection: McpApiKey;
};

/**
 * A plain confirmation, not the API key modal's type-the-name-back check. That
 * check guards a key some system somewhere still calls with, where revoking
 * breaks it silently and nothing brings the key back. A revoked MCP
 * connection is visible to the one person using it, and they restore it by
 * connecting again.
 */
const RevokeMcpConnectionModalContent: React.FC<RevokeMcpConnectionModalContentProps> =
  function RevokeMcpConnectionModalContent({
    open,
    connection,
    onClose,
    onSuccess,
  }) {
    const { t } = useTranslation();

    const deleteMutation = interlude.mcp.useDeleteMcpApiKey(
      { params: { credentialId: connection.id } },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
        /**
         * A 404 means the connection is already gone, revoked from another
         * tab or by someone else. The global handler reports it, and the
         * zodios wrapper still reloads the list on a 4xx, so all that is left
         * is to stop offering to revoke a row that is about to disappear.
         * Every other refusal keeps the modal open for another try.
         */
        onError: (error) => {
          if (
            getApiErrorBodyFromUnknownError(error)?.code ===
            "OPENAPI_CREDENTIAL_NOT_FOUND"
          ) {
            onClose();
          }
        },
      },
    );

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      },
    );

    const handleSubmit = useHandler(() => {
      deleteMutation.mutate(undefined);
    });

    const footer: React.ReactNode = useMemo(
      () => (
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={onClose}>
            {t("mcpConnection.revokeModal.cancel", "Cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={deleteMutation.isPending}
            loading={deleteMutation.isPending}
          >
            {t("mcpConnection.revokeModal.submit", "Revoke connection")}
          </Button>
        </Stack>
      ),
      [onClose, handleSubmit, deleteMutation.isPending, t],
    );

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={t("mcpConnection.revokeModal.title", "Revoke MCP connection")}
        width={520}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          <InfoBox
            variant="warning"
            title={t(
              "mcpConnection.revokeModal.warning.title",
              "This connection stops working immediately",
            )}
            description={
              <Box component="ul" sx={styles.warningList}>
                <li>
                  <Typography variant="body" color="inherit">
                    {t(
                      "mcpConnection.revokeModal.warning.immediate",
                      "The AI client can no longer reach your organization's data through Crescendo Lab MCP.",
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body" color="inherit">
                    {t(
                      "mcpConnection.revokeModal.warning.reconnect",
                      "To use it again, the person who authorized it has to authorize Crescendo Lab MCP again from their AI client, for example Claude.",
                    )}
                  </Typography>
                </li>
              </Box>
            }
          />

          <Box sx={sharedStyles.formSection}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgBody}
            >
              {t("mcpConnection.revokeModal.connectionLabel", "Connection")}
            </Typography>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {connection.name}
            </Typography>
          </Box>
        </Stack>
      </Modal>
    );
  };

export const RevokeMcpConnectionModal: React.FC<RevokeMcpConnectionModalProps> =
  function RevokeMcpConnectionModal({ connection, ...props }) {
    if (!connection) return null;
    return (
      <RevokeMcpConnectionModalContent connection={connection} {...props} />
    );
  };
